import { createTheme } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';

import { borders } from './borders';
import { breakpoints } from './breakpoints';
import { generateCoreTokens } from './generateCoreTokens';
import { palette } from './palette';
import { shadows } from './shadows';
import { spacing, compactSpacing } from './spacing';
import { getComponentTokens, unbrandedCore, voltCore } from './tokens';
import { typography, typographyToSystem } from './typography';
import { zIndex } from './zIndex';
import { zoom } from './zoom';

export const theme = createTheme({
  iconSize: {
    sm: '1.6rem',
    md: '2.4rem',
    lg: '3.2rem',
    xl: '5.6rem',
  },
  palette,
  typography,
  spacing,
  borders,
  zIndex,
  breakpoints,
  shadows,
  tokens: getComponentTokens(voltCore),
  coreTokens: voltCore,
  voltBranding: {
    poweredBy: true,
    appLogo: true,
  },
  zoom,
});

export const SMALL_PHONE_RULE = '(max-height: 800px)';
export const SMALL_PHONE_MEDIA_QUERY = `@media${SMALL_PHONE_RULE}`;

export const unbrandedTheme = createTheme({
  ...theme,
  tokens: getComponentTokens(unbrandedCore),
  coreTokens: unbrandedCore,
  voltBranding: {
    poweredBy: false,
    appLogo: false,
  },
});

export type BrandedThemeParams = {
  colors?: {
    primary?: string;
    secondary?: string;
  };
  logos?: {
    app?: string;
  };
  isVoltBrandingHidden?: boolean;
};

export const brandedTheme = (
  brandingConfig: BrandedThemeParams | null | undefined,
  baseTheme: ThemeOptions,
  overwrites?: Partial<ThemeOptions>
) => {
  const primaryColor = brandingConfig?.colors?.primary;
  const secondaryColor = brandingConfig?.colors?.secondary;
  const logos = brandingConfig?.logos;
  const showVoltAppLogo = !brandingConfig?.isVoltBrandingHidden;
  const voltBranding = { ...baseTheme.voltBranding, appLogo: showVoltAppLogo };

  if (primaryColor) {
    const coreTokens = generateCoreTokens(
      primaryColor,
      secondaryColor ?? primaryColor
    );
    return createTheme({
      ...baseTheme,
      tokens: getComponentTokens(coreTokens),
      coreTokens,
      logos,
      voltBranding,
      ...overwrites,
    });
  }

  return createTheme({
    ...baseTheme,
    logos,
    voltBranding,
    ...overwrites,
  });
};

export const convertToColorWithOpacityInHex = (
  color: string,
  opacity: number
) => {
  const opacityInHex = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');
  return `${color}${opacityInHex}`;
};

export { compactSpacing, typographyToSystem };
